import React, { useState } from 'react';
import PropTypes from 'prop-types';

const DragDropList = ({ items, onReorder, renderItem, className = '' }) => {
  const [draggedItem, setDraggedItem] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    // Set the drag image to the entire question item
    const dragItem = e.target.closest('.drag-drop-item');
    if (dragItem) {
      e.dataTransfer.setDragImage(dragItem, e.offsetX, e.offsetY);
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDraggedOverIndex(index);
  };

  const handleDragLeave = () => {
    setDraggedOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null || draggedItem === dropIndex) {
      setDraggedItem(null);
      setDraggedOverIndex(null);
      return;
    }

    const newItems = [...items];
    const draggedItemData = newItems[draggedItem];
    
    // Remove the dragged item
    newItems.splice(draggedItem, 1);
    
    // Insert at new position
    const adjustedDropIndex = draggedItem < dropIndex ? dropIndex - 1 : dropIndex;
    newItems.splice(adjustedDropIndex, 0, draggedItemData);
    
    onReorder(newItems);
    
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDraggedOverIndex(null);
  };

  return (
    <div className={`drag-drop-list ${className}`}>
      {items.map((item, index) => {
        const isDragging = draggedItem === index;
        const isDraggedOver = draggedOverIndex === index;
        
        return (
          <div
            key={item.id || index}
            className={`drag-drop-item ${isDragging ? 'dragging' : ''} ${isDraggedOver ? 'drag-over' : ''}`}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
          >
            <div 
              className="drag-handle"
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              title="Drag to reorder"
            >
              <i className="fa fa-bars"></i>
            </div>
            <div className="item-content">
              {renderItem(item, index)}
            </div>
          </div>
        );
      })}
      
      <style>{`
        .drag-drop-list {
          position: relative;
        }
        
        .drag-drop-item {
          display: flex;
          align-items: stretch;
          margin-bottom: 0.5rem;
          border: 1px solid #dee2e6;
          border-radius: 0.25rem;
          background: white;
          transition: all 0.2s ease;
        }
        
        .drag-drop-item:hover {
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .drag-drop-item.dragging {
          opacity: 0.5;
          transform: rotate(2deg);
        }
        
        .drag-drop-item.drag-over {
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0,123,255,0.25);
        }
        
        .drag-handle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 2.5rem;
          background: #f8f9fa;
          border-right: 1px solid #dee2e6;
          color: #adb5bd;
          font-size: 1rem;
          cursor: grab;
          transition: all 0.2s ease;
          border-top-left-radius: 0.25rem;
          border-bottom-left-radius: 0.25rem;
        }
        
        .drag-handle:hover {
          color: #495057;
          background: #e9ecef;
        }
        
        .drag-handle:active {
          cursor: grabbing;
        }
        
        .item-content {
          flex: 1;
          min-width: 0;
          cursor: default;
        }
      `}</style>
    </div>
  );
};

DragDropList.propTypes = {
  items: PropTypes.array.isRequired,
  onReorder: PropTypes.func.isRequired,
  renderItem: PropTypes.func.isRequired,
  className: PropTypes.string
};

export default DragDropList;